import { TFunction } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { AxiosError } from "axios";
import { z } from "zod";

import {
    ControlResultOperationFailedError,
    ControlResultOverviewListDto,
} from "api/generated/model";
import { ReassignmentFailure } from "org/user/components/ReassignmentFailureAlert";
import { STATUS_CODE_ERROR_OPERATION_FAILED } from "utils/errorCodes";

const controlResultOperationFailedErrorSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("SegregationOfDutiesViolated"),
        data: z.array(z.string()),
    }),
    z.object({
        type: z.literal("EmptyUserPool"),
        data: z.array(
            z.object({
                controlResultId: z.string(),
                userPoolId: z.string(),
            })
        ),
    }),
]) satisfies z.ZodType<ControlResultOperationFailedError>;

export function describeControlResultFailure(
    t: TFunction,
    error: unknown,
    data: ControlResultOverviewListDto
): ReassignmentFailure | null {
    if (
        !(error instanceof AxiosError) ||
        error.response?.status !== STATUS_CODE_ERROR_OPERATION_FAILED
    ) {
        return null;
    }
    const parsed = controlResultOperationFailedErrorSchema.safeParse(
        error.response.data
    );
    if (!parsed.success) {
        return null;
    }
    const body = parsed.data;
    const titleOf = (id: string) =>
        data.content.find((dto) => dto.resource.id === id)?.resource.title ??
        id;
    const poolNameOf = (id: string) =>
        data.linkedResources.controlResultUserPools.find(
            (pool) => pool.id === id
        )?.name ?? id;

    switch (body.type) {
        case "SegregationOfDutiesViolated":
            return {
                message: t(
                    "AccessAndAssignment_ReassignError_SegregationOfDuty_Activities"
                ),
                items: body.data.map(titleOf),
            };
        case "EmptyUserPool":
            return {
                message: t("AccessAndAssignment_ReassignError_EmptyUserPool"),
                items: body.data.map((detail) =>
                    t(
                        "ControlResult_Error_EmptyUserPool_ControlResultInGroup",
                        {
                            controlResultTitle: titleOf(detail.controlResultId),
                            poolName: poolNameOf(detail.userPoolId),
                        }
                    )
                ),
            };
        default:
            return assertNever(body);
    }
}
